import keyBy from 'lodash/keyBy';
import ObjectUtils from '@spothero/utils/object';
import {spotheroGet} from 'api';
import {sendSentryException} from 'utils/error-handling';

/**
 * Formats the response from the get rating rule groups endpoint
 * @param {array} response
 * @return {TRatingRuleGroup[]}
 *
 * @memberof module:api/rates/dto
 */
const formatGetRatingRuleGroupsResponse = (response) => {
    const formattedResponse = ObjectUtils.camelizeKeys(response);

    return formattedResponse;
};

/**
 * Gets rating rule groups for a facility
 * @param {number} facilityId - The facility id to get rating rule groups for
 * @return {Promise<TRatingRuleGroup[]>}
 *
 * @throws {Error}
 *
 * @see '/api/v1/facilities/{facilityId}/rating-rule-groups/'
 * @function getRatingRuleGroups
 */
export const getRatingRuleGroups = async (facilityId) => {
    try {
        const response = await spotheroGet(
            `/facilities/${facilityId}/rating-rule-groups/`
        );

        const formattedResponse = formatGetRatingRuleGroupsResponse(
            response?.data?.data?.results
        );

        return formattedResponse;
    } catch (error) {
        sendSentryException(error);

        throw error;
    }
};

/**
 * Format the given response object to get the enabled rating rule groups.
 *
 * @param {Object} response - The response object containing rating rule groups.
 * @returns {Object[]} - An array of enabled rating rule groups.
 *
 * @memberof module:api/rates/dto
 */
const formatGetEnabledRatingRuleGroupsResponse = (response) => {
    const formattedResponse = ObjectUtils.camelizeKeys(response);
    const ruleGroups = Object.values(formattedResponse?.ruleGroups).map(
        (ruleGroup) => ruleGroup
    );

    return ruleGroups;
};

/**
 * Retrieves the enabled rating rule groups for a specific facility.
 *
 * @param {string} facilityId - The ID of the facility.
 * @throws {Error} If there is an error retrieving the data.
 * @returns {Promise<Array>} A promise that resolves to an array of enabled rating rule groups.
 *
 * @see '/api/v1/ratings/facilities/{facilityId}/rates/'
 * @function getEnabledRatingRuleGroups
 */
export const getEnabledRatingRuleGroups = async (facilityId) => {
    try {
        const response = await spotheroGet(
            `/ratings/facilities/${facilityId}/rates/`
        );

        const formattedResponse = formatGetEnabledRatingRuleGroupsResponse(
            response?.data?.data
        );

        return formattedResponse;
    } catch (error) {
        sendSentryException(error);

        throw error;
    }
};

/**
 * Formats the response of transient pricing algorithms.
 *
 * @param {Object} response - The response object.
 * @returns {Object} - The formatted pricing algorithms.
 *
 * @memberof module:api/rates/dto
 */
const formatGetTransientPricingAlgorithmsResponse = (response) => {
    const formattedResponse = ObjectUtils.camelizeKeys(response);
    const pricingAlgorithms = keyBy(formattedResponse, 'facilityId');

    return pricingAlgorithms;
};

/**
 * Retrieves the transient pricing algorithms for the given facility IDs.
 *
 * @param {Array} facilityIds - An array of facility IDs.
 *
 * @returns {Promise<{transienPricingAlgorithms: Array<strings>}>} - A promise that resolves to the formatted transient pricing algorithms response.
 *
 * @throws {Error} - If an error occurs during the retrieval.
 *
 * @see '/api/v1/ratings/facilities/rates/'
 * @function getTransientPricingAlgorithms
 */
export const getTransientPricingAlgorithms = async (facilityIds) => {
    try {
        const response = await spotheroGet(
            `/ratings/facilities/rates/`,
            // eslint-disable-next-line camelcase
            {facility_ids: facilityIds.toString()}
        );

        const formattedResponse = formatGetTransientPricingAlgorithmsResponse(
            response?.data?.data?.results
        );

        return formattedResponse;
    } catch (error) {
        sendSentryException(error);

        throw error;
    }
};
